import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Markdown } from '@/components/ui/markdown';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { GoalStatus, PDPGoal } from '@/features/hrm/development/api';
import { pdpStatusLabel, pdpStatusVariant } from '@/features/hrm/development/constants';
import { useCabinetPdp, useSetCabinetPdpGoalStatus } from '../queries';

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

const GOAL_STATUSES: readonly { value: GoalStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

/** My Space → Development plan → read one (read-only). */
export function CabinetPdpDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const { data: pdp, isPending, isError } = useCabinetPdp(publicId);
  const setGoalStatus = useSetCabinetPdpGoalStatus();

  const back = () => navigate('/me/pdp');

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !pdp) {
    return (
      <div className="max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={back}>
          <ArrowLeft /> Development plan
        </Button>
        <p className="text-muted-foreground">This plan isn’t available.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2" onClick={back}>
        <ArrowLeft /> Development plan
      </Button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{pdp.title}</h1>
        <Badge variant={pdpStatusVariant(pdp.status)}>{pdpStatusLabel(pdp.status)}</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Title">{pdp.title}</Field>
          <Field label="Period">{pdp.period || '—'}</Field>
          <Field label="Status">{pdpStatusLabel(pdp.status)}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {pdp.summary ? (
            <Markdown>{pdp.summary}</Markdown>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Goals</CardTitle>
        </CardHeader>
        <CardContent>
          {pdp.goals.length === 0 ? (
            <p className="text-muted-foreground text-sm">No goals.</p>
          ) : (
            <ul className="space-y-4">
              {pdp.goals.map((g) => (
                <GoalRow
                  key={g.id}
                  goal={g}
                  pending={setGoalStatus.isPending && setGoalStatus.variables?.id === g.id}
                  onStatusChange={(status) => setGoalStatus.mutate({ id: g.id, status })}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GoalRow({
  goal,
  pending,
  onStatusChange,
}: {
  goal: PDPGoal;
  pending: boolean;
  onStatusChange: (status: GoalStatus) => void;
}) {
  return (
    <li className="space-y-1">
      <div className="flex items-center gap-3 text-sm">
        <span className="min-w-0 flex-1 font-medium">{goal.title}</span>
        {goal.due_date ? (
          <span className="text-muted-foreground hidden text-xs sm:inline">
            due {fmtDate(goal.due_date)}
          </span>
        ) : null}
        <Select
          value={goal.status}
          onValueChange={(v) => onStatusChange(v as GoalStatus)}
          disabled={pending}
        >
          <SelectTrigger className="w-40 shrink-0" aria-label="Goal status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GOAL_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {goal.description ? (
        <p className="text-muted-foreground text-sm whitespace-pre-line">{goal.description}</p>
      ) : null}
    </li>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
